import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';
import styled, { css } from 'styled-components';

import Button, { RemoveButton } from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import { Input, RadioCircle, RadioInput, Textarea } from '@/components/Forms';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';
import { scrollBarStyles } from '@/utils/theme';

import AttachedCountBadge from '../AttachedCountBadge';
import MoreImagesModal from '../MoreImagesModal';
import UploadMultipleImages from '../UploadMultipleImages';
import {
  AddMoreButton,
  Box,
  ButtonHolder,
  Header,
  HeaderButtons,
  Heading,
  ImageData,
  LabelText,
  MoreImages,
  ResetButton,
  UploadedImages,
} from '../VariationOne';
import { getWatermarkPosition, getWatermarkTag } from '../shared';
import Filler from '../shared/Filler';
import { StyledRadiosLabel } from '../shared/styledComponents';
import GeneratedImages from './GeneratedImages';
import UploadedImageRow from './UploadedImageRow';
import VariationStyles from './VariationStyles';

const DEFAULT_FONT_SIZE = 44;

export const DIVIDERS = ['none', 'black'];

export interface ImageVariation {
  imageSrc: string;
  watermarkTag: string;
  watermarkPosition: string;
  fontSize: number;
  divider: string;
  chats: {
    bottomText: string;
    color: 'blue' | 'grey';
    brakes: boolean;
  }[];
}

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
`;

const TextItem = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TextAreaBox = styled.div`
  padding: 24px;
  border-left: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 16px;
  flex-grow: 1;
  align-content: start;
`;

const StyledTextArea = styled(Textarea)`
  height: 80px;
`;

const StyledUploadMultipleImages = styled(UploadMultipleImages)`
  height: calc(100% - 24px);
`;

const StyledUploadedImages = styled(UploadedImages)`
  max-height: none;
  height: calc(100% - 94px);
  min-height: 200px;
`;

const LabelHolder = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const AdditionalBox = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
`;

const ImagesBox = styled.div<{ $hasImages?: boolean }>`
  padding: 24px;
  ${({ $hasImages }) =>
    $hasImages &&
    css`
      padding-bottom: 0;
      border-bottom: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
    `}
`;

const TextRight = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 24px;
`;

const StyledRemoveButton = styled(RemoveButton)`
  background: transparent;
  height: auto;
  padding: 0;
  margin-bottom: 8px;
  text-decoration: underline;
  transition: all 0.2s;
  &:hover {
    border-color: transparent;
    opacity: 0.8;
  }
`;

const CounterBadge = styled(Text)`
  background: ${({ theme }) => theme.colors.purple};
  width: 20px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AddMoreHolder = styled.div<{ $inABox?: boolean }>`
  display: flex;
  padding: 16px 24px;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  ${({ $inABox }) =>
    $inABox &&
    css`
      padding-bottom: 0;
      margin-left: -24px;
      margin-right: -24px;
      margin-bottom: -8px;
    `}
`;

const StyeldAddMoreButton = styled(AddMoreButton)`
  margin-left: auto;
`;

const BottomTextsHolder = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 16px;
  padding-right: 8px;
  max-height: 697px;
  overflow-y: auto;
  ${scrollBarStyles}
`;

const RadioGroup = styled.div`
  width: 130px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

interface Props {
  inArticle?: boolean;
}

type BottomTextItem = {
  text: string;
  brakes: boolean;
  color: 'blue' | 'grey';
};

const VariationImgChats: FC<Props> = ({ inArticle }) => {
  const [showContent, setShowContent] = useState(true);

  const [bottomTexts, setBottomTexts] = useState<BottomTextItem[]>([
    { text: '', brakes: false, color: 'blue' },
  ]);

  const [fullImages, setFullImages] = useState<ImageData[]>([]);
  const [showMoreImagesModal, setShowMoreImagesModal] = useState(false);
  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [images, setImages] = useState<string[]>();
  const [fontSize, setFontSize] = useState(DEFAULT_FONT_SIZE);

  // ---- Bottom text handlers ----
  const addBottomText = () =>
    setBottomTexts((prev) => [
      ...prev,
      { text: '', brakes: false, color: 'blue' },
    ]);

  const updateBottomText = (idx: number, value: string) =>
    setBottomTexts((prev) =>
      prev.map((b, i) => (i === idx ? { ...b, text: value } : b)),
    );

  const updateChatColor = (idx: number, color: 'blue' | 'grey') =>
    setBottomTexts((prev) =>
      prev.map((b, i) => (i === idx ? { ...b, color } : b)),
    );

  const toggleBottomTextBrakes = (idx: number) =>
    setBottomTexts((prev) =>
      prev.map((b, i) => (i === idx ? { ...b, brakes: !b.brakes } : b)),
    );

  const removeBottomText = (idx: number) =>
    setBottomTexts((prev) => prev.filter((_, i) => i !== idx));

  const onReset = () => {
    setFontSize(DEFAULT_FONT_SIZE);
    setFullImages([]);
    setVariations([]);
    setImages([]);
    setBottomTexts([{ text: '', brakes: false, color: 'blue' }]);
  };

  const generateVariations = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const newVariations: ImageVariation[] = fullImages.flatMap((image) => {
      const imageSrc = image.croppedImage ?? URL.createObjectURL(image.image);
      const divider = image.divider;

      return {
        imageSrc,
        watermarkTag: getWatermarkTag(image),
        watermarkPosition: getWatermarkPosition(image),
        fontSize: fontSize,
        divider,
        chats: bottomTexts.map((b) => ({
          bottomText: b.text,
          color: b.color,
          brakes: b.brakes,
        })),
      };
    });

    setVariations(newVariations);
  };

  const renderFullImages = fullImages.map((image, index) => (
    <UploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setFullImages}
    />
  ));

  const isResetDisabled =
    !fullImages?.length &&
    !bottomTexts.length &&
    fontSize !== DEFAULT_FONT_SIZE;

  return (
    <>
      <VariationStyles />
      <Box>
        <Filler fontFamily="SourceSansProRegular" />
        <Header>
          <div>
            <Heading $type="button">Image + Chats</Heading>
            <Text $type="smallHeadline" $color="gray">
              Add cover and chats
            </Text>
          </div>
          <HeaderButtons>
            <ResetButton
              color="red"
              disabled={isResetDisabled}
              onClick={onReset}
            >
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>
        {showContent && (
          <>
            <ImageGrid>
              <ImagesBox $hasImages={!!fullImages?.length}>
                <LabelText $type="smallHeadline">Images</LabelText>
                {!!fullImages?.length ? (
                  <>
                    <StyledUploadedImages>
                      {renderFullImages}
                    </StyledUploadedImages>
                    <MoreImages>
                      <AttachedCountBadge count={fullImages.length} />
                      <AddMoreButton
                        onClick={() => setShowMoreImagesModal(true)}
                        color="colorized"
                      >
                        Add more images
                      </AddMoreButton>
                    </MoreImages>
                  </>
                ) : (
                  <StyledUploadMultipleImages
                    coverIndex={2}
                    noneDivider
                    setImages={setFullImages}
                  />
                )}
              </ImagesBox>
              <TextAreaBox>
                <BottomTextsHolder>
                  {bottomTexts.map((b, idx) => (
                    <TextItem key={idx}>
                      <div>
                        <LabelHolder>
                          <LabelText $type="smallHeadline">
                            Chat Text
                            {bottomTexts.length > 1 && (
                              <CounterBadge $type="extraSmall" $color="white">
                                {idx + 1}
                              </CounterBadge>
                            )}
                          </LabelText>
                          {bottomTexts.length > 1 && (
                            <StyledRemoveButton
                              color="red"
                              onClick={() => removeBottomText(idx)}
                            >
                              Remove
                            </StyledRemoveButton>
                          )}
                        </LabelHolder>
                        <StyledTextArea
                          onChange={(e) =>
                            updateBottomText(idx, e.target.value)
                          }
                          value={b.text}
                          placeholder="Chat text"
                        />
                      </div>

                      <TextRight>
                        <RadioGroup>
                          <StyledRadiosLabel>
                            Blue
                            <RadioInput
                              type="radio"
                              name={`chat_color_${idx}`}
                              checked={b.color === 'blue'}
                              onChange={() => updateChatColor(idx, 'blue')}
                            />
                            <RadioCircle />
                          </StyledRadiosLabel>
                          <StyledRadiosLabel>
                            Grey
                            <RadioInput
                              type="radio"
                              name={`chat_color_${idx}`}
                              checked={b.color === 'grey'}
                              onChange={() => updateChatColor(idx, 'grey')}
                            />
                            <RadioCircle />
                          </StyledRadiosLabel>
                        </RadioGroup>
                        <TextRight>
                          <Checkbox
                            checked={!!b.brakes}
                            onChange={() => toggleBottomTextBrakes(idx)}
                          >
                            Custom brakes
                          </Checkbox>
                        </TextRight>
                      </TextRight>
                    </TextItem>
                  ))}
                </BottomTextsHolder>
                <AddMoreHolder $inABox>
                  {bottomTexts.length > 1 && (
                    <AttachedCountBadge
                      count={bottomTexts.length}
                      text="Chat Texts"
                    />
                  )}
                  <StyeldAddMoreButton
                    color="colorized"
                    onClick={addBottomText}
                  >
                    Add chat text
                  </StyeldAddMoreButton>
                </AddMoreHolder>
              </TextAreaBox>
            </ImageGrid>
            <AdditionalBox>
              <LabelText $type="smallHeadline">Font Size, px</LabelText>
              <Input
                type="number"
                value={fontSize ?? ''}
                onChange={(e) => setFontSize(parseInt(e.target.value))}
                placeholder="Add Font Size"
              />
            </AdditionalBox>
          </>
        )}
      </Box>

      {showContent && (
        <ButtonHolder>
          <Button disabled={!fullImages?.length} onClick={generateVariations}>
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
        </ButtonHolder>
      )}

      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />

      <AnimatePresence>
        {!!showMoreImagesModal && (
          <MoreImagesModal
            onClose={() => setShowMoreImagesModal(false)}
            setImages={setFullImages}
            coverIndex={1}
            noneDivider
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default VariationImgChats;
